import type { AxiosError } from 'axios'

import { serverErrorMessage } from '@/shared/api'

import { message } from './message'

/**
 * Whether the response interceptor has already spoken about this failure. It raises a
 * toast for 5xx (`src/shared/api/interceptors.ts`) and stays quiet about everything
 * else — every 4xx, and a failure with no response at all (a dropped connection, the
 * 15s timeout).
 */
export function isGloballyReported(error: unknown): boolean {
  const status = (error as AxiosError | undefined)?.response?.status

  return status !== undefined && status >= 500
}

/**
 * Tell the user a mutation was refused, in the server's own words — **every** mutation's
 * `onError` is this call:
 *
 * ```ts
 * remove.mutate(id, { onError: (error) => showMutationError(error, 'Could not delete the record.') })
 * ```
 *
 * It is what keeps the two failure modes apart, and both are easy to hit by hand: no
 * `onError` at all is silent below 500 (the row just does not change and the API's reason
 * is thrown away), while an `onError` that always toasts shows *two* messages on a 5xx,
 * because the interceptor has already shown one and toasts do not group.
 *
 * The fallback is the caller's: only it knows what the user was doing.
 *
 * `retry` adds a "Try again" button to the toast — pass it wherever running the same
 * mutation again is the whole recovery. It cannot reach a 5xx, which returns early here, so
 * what it actually offers a way out of is the failure with no response at all: a dropped
 * connection or the request timeout, which is exactly the transient case.
 */
export function showMutationError(
  error: unknown,
  fallback: string,
  options?: { retry?: () => void }
): void {
  if (isGloballyReported(error)) return

  message.error(serverErrorMessage(error, fallback), {
    action: options?.retry
      ? { label: 'Try again', onClick: options.retry }
      : undefined
  })
}
